import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Zap, Loader2, Download, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'unpaid', label: 'Unpaid' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'approaching', label: 'Approaching' },
  { key: 'all', label: 'All' },
];

const statusChip = (status) => {
  switch (status) {
    case 'overdue':
      return { cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle, label: 'Overdue' };
    case 'approaching':
      return { cls: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Due soon' };
    case 'paid':
      return { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Paid' };
    default:
      return { cls: 'bg-slate-100 text-slate-600', icon: Clock, label: 'On track' };
  }
};

/**
 * CriticalPayQueueCard — embedded in the accounting/payouts area. Surfaces
 * Critical Pay bookings awaiting payout, prioritized (overdue first), with the
 * 7-business-day due date, approaching/overdue status and CSV export.
 */
const CriticalPayQueueCard = () => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unpaid');
  const [data, setData] = useState({ queue: [], total: 0, overdue: 0, approaching: 0, unpaid: 0 });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/payout-terms/critical-pay/queue', {
        params: f && f !== 'all' ? { filter: f } : {},
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load Critical Pay queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/payout-terms/critical-pay/queue', {
        params: { format: 'csv', ...(filter !== 'all' ? { filter } : {}) },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'critical_pay_payouts.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="critical-pay-queue-card" className="border-amber-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Critical Pay Priority Queue
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex gap-3 text-xs">
              <span className="text-rose-600 font-semibold" data-testid="cpq-overdue-count">{data.overdue} overdue</span>
              <span className="text-amber-600 font-semibold" data-testid="cpq-approaching-count">{data.approaching} due soon</span>
              <span className="text-slate-600 font-semibold" data-testid="cpq-unpaid-count">{data.unpaid} unpaid</span>
            </div>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} data-testid="cpq-export-csv">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1.5 mb-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`cpq-filter-${f.key}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                filter === f.key ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : (data.queue || []).length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500" data-testid="cpq-empty">
            No Critical Pay payouts in this view.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b">
                  <th className="py-2 pr-2">Talent</th>
                  <th className="py-2 pr-2">Event</th>
                  <th className="py-2 pr-2">Approved</th>
                  <th className="py-2 pr-2">Due (7 bd)</th>
                  <th className="py-2 pr-2">Days left</th>
                  <th className="py-2 pr-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.queue.map((r) => {
                  const chip = statusChip(r.status);
                  const Icon = chip.icon;
                  return (
                    <tr key={r.booking_id} className="border-b border-slate-50" data-testid={`cpq-row-${r.booking_id}`}>
                      <td className="py-2 pr-2 font-medium text-slate-800">{r.talent_name}</td>
                      <td className="py-2 pr-2 text-slate-600 max-w-[180px] truncate">{r.event_title || r.venue_name}</td>
                      <td className="py-2 pr-2 text-slate-500">{r.timesheet_approved_at ? new Date(r.timesheet_approved_at).toLocaleDateString() : '—'}</td>
                      <td className="py-2 pr-2 text-slate-700 font-medium">{r.critical_pay_due_date ? new Date(r.critical_pay_due_date).toLocaleDateString() : '—'}</td>
                      <td className={`py-2 pr-2 font-semibold ${r.days_remaining < 0 ? 'text-rose-600' : r.days_remaining <= 2 ? 'text-amber-600' : 'text-slate-600'}`}>
                        {r.paid ? '—' : `${r.days_remaining}d`}
                      </td>
                      <td className="py-2 pr-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chip.cls}`}>
                          <Icon className="w-3 h-3" /> {chip.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CriticalPayQueueCard;
